import React, { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { merge } from 'lodash-es';
import PropTypes from 'prop-types';

import { updateUserResponses } from '../../../../bridge';
import { logError } from '../../../../helpers/logger';
import { __ } from '../../../../helpers/translations';
import CompleteModal from '../../common/CompleteModal';

import Caption from './inputs/Caption';
import Consent from './inputs/Consent';
import ConsentOptions from './inputs/ConsentOptions';
import LeadershipText from './inputs/LeadershipText';
import MultipleChoice from './inputs/MultipleChoice';
import PeerRanged from './inputs/PeerRanged';
import PeerRangedText, { isFacilitatorEligibleItem } from './inputs/PeerRangedText';
import TeamText from './inputs/TeamText';
import { defaultPeers } from './PreviewDefaults';

function SurveyBody({
  template, surveyId, respondentId, pageIndex, isPreview, respondent, peers,
}) {
  const { templateId, pages } = template;
  const currentPage = pages[pageIndex];
  const [showComplete, setShowComplete] = useState(false);
  const [response, setResponse] = useState((respondent && respondent.response) || {});
  const [changes, setChanges] = useState({});
  const currentResponse = merge({}, response, changes);
  const navigateWithContext = (destPageIndex) => {
    let url = `/respondent/${respondentId}/${destPageIndex}`;
    if (isPreview) {
      url = `/template/${templateId}/${destPageIndex}`;
    }
    window.location.href = url;
  };

  const updateResponses = async () => {
    if (Object.keys(changes).length === 0) {
      return;
    }

    if (!isPreview) {
      console.log('[SurveyBody] updateResponses -> PUT /api/respondent/:respondentId', {
        respondentId,
        pageIndex,
        changeKeys: Object.keys(changes),
      });
      const { error } = await updateUserResponses(respondentId, changes);
      if (error) {
        logError('survey_body_update_responses', error);
        return;
      }
    } else {
      console.log('[SurveyBody] preview mode active, skipping PUT /api/respondent/:respondentId', {
        templateId,
        pageIndex,
        changeKeys: Object.keys(changes),
      });
    }
    setResponse((prev) => merge({}, prev, changes));
    setChanges({});
  };

  const handleNext = async (skip) => {
    console.log('[SurveyBody] handleNext clicked', {
      respondentId,
      templateId,
      pageIndex,
      isPreview,
      skip,
      isLastPage: pageIndex + 1 >= pages.length,
    });
    await updateResponses();
    const nextPageIndex = pageIndex + 1;
    if (nextPageIndex >= pages.length) {
      setShowComplete(true);
    } else {
      navigateWithContext(nextPageIndex + (skip ? 1 : 0));
    }
  };

  const handleBack = async () => {
    console.log('[SurveyBody] handleBack clicked', {
      respondentId,
      templateId,
      pageIndex,
      isPreview,
    });
    await updateResponses();
    navigateWithContext(Math.max(pageIndex - 1, 0));
  };

  useEffect(() => {
    updateResponses();
  }, [changes]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageIndex]);

  useEffect(() => {
    if (respondent.status === 'completed') {
      setShowComplete(true);
    }
  }, []);

  return (
    <>
      <CompleteModal
        show={showComplete}
        onHide={() => setShowComplete(false)}
        respondentId={respondentId}
        completed={respondent.status === 'completed'}
      />
      <Card className="my-2">
        <Card.Body>
          <Caption data={currentPage} response={response} />

          <TeamText
            data={currentPage}
            response={currentResponse}
            onChanged={(delta) => setChanges(merge({ ...changes }, delta))}
          />

          <LeadershipText
            data={currentPage}
            response={currentResponse}
            onChanged={(delta) => setChanges(merge({ ...changes }, delta))}
          />

          <MultipleChoice
            data={currentPage}
            response={currentResponse}
            setResponse={setResponse}
            changes={changes}
            setChanges={setChanges}
          />

          <Consent
            data={currentPage}
            response={currentResponse}
            onChanged={(delta) => setChanges(merge({ ...changes }, delta))}
          />

          <ConsentOptions
            data={currentPage}
            response={currentResponse}
            onChanged={(delta) => setChanges(merge({ ...changes }, delta))}
          />

          <PeerRanged
            data={currentPage}
            peers={peers.length === 0 ? defaultPeers : peers}
            response={currentResponse}
            onChanged={(delta) => setChanges(merge({ ...changes }, delta))}
          />

          <PeerRangedText
            templateId={templateId}
            surveyId={surveyId}
            respondentId={respondentId}
            isPreview={isPreview}
            data={currentPage}
            peers={peers.length === 0 ? defaultPeers.filter((peer) => peer.firstName !== 'Self') : peers.filter((peer) => peer.respondentId !== respondentId)}
            response={currentResponse}
            onChanged={(delta) => setChanges(merge({ ...changes }, delta))}
            onAllSent={() => handleNext(false)}
          />

        </Card.Body>
        <Card.Footer>
          {pageIndex > 0 && (
          <Button variant="outline-secondary" onClick={handleBack} aria-label={__('G48')}>
            {__('G48')}
          </Button>
          )}

          {currentPage[0].skipNext && (
          <Button onClick={() => handleNext(true)} variant="secondary" className="float-end mx-2" aria-label={__('G50')}>
            {currentPage[0].skipNextText}
          </Button>
          )}
          {/* 2026-06-05 (McKenna UX): on the AI-coaching comments page, hide the
              page-level Next button so users can't click through without
              engaging with the per-teammate Send Feedback / Not familiar flow.
              Advance happens automatically once every teammate is done
              (PeerRangedText fires onAllSent -> handleNext). Back stays visible.

              This hides Next ONLY for facilitator-eligible Qualitative pages --
              the exact pages where that auto-advance + Send-feedback UI renders
              (isFacilitatorEligibleItem, shared with PeerRangedText). Every other
              template (peerFeedback, peerFeedbackTeamDynamics, teamPlayerFacets,
              ...) shows no coaching UI and never fires onAllSent, so it MUST keep
              the standard Next button -- otherwise the respondent is trapped on
              the comments page with no way forward. */}
          {!currentPage.some((item) => isFacilitatorEligibleItem(templateId, item)) && (
          <Button
            onClick={() => handleNext(false)}
            className="float-end"
            aria-label={pageIndex === pages.length - 1
              ? __('G49')
              : __('G47')}
          >
            {pageIndex === pages.length - 1
              ? __('G49')
              : __('G47')}
          </Button>
          )}
        </Card.Footer>
      </Card>
    </>
  );
}

SurveyBody.propTypes = {
  template: PropTypes.shape({
    templateId: PropTypes.string,
    name: PropTypes.string,
    pages: PropTypes.arrayOf(PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string,
        content: PropTypes.string,
        skipNext: PropTypes.bool,
        skipNextText: PropTypes.string,
      }),
    )),
  }),
  surveyId: PropTypes.string,
  respondentId: PropTypes.string,
  pageIndex: PropTypes.number,
  isPreview: PropTypes.bool,
  respondent: PropTypes.shape({
    response: PropTypes.object,
    status: PropTypes.string,
  }),
  peers: PropTypes.arrayOf(PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    respondentId: PropTypes.string,
  })),
};

SurveyBody.defaultProps = {
  template: null,
  surveyId: null,
  respondentId: null,
  pageIndex: 0,
  isPreview: false,
  respondent: {},
  peers: [],
};

export default SurveyBody;
